import { h, render } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import './styles/main.scss';
import { MiniCartRecommendationWidget } from './pages/MiniCartRecommendationWidget';
// `window.mrRecommendations.refresh` is installed as a side effect of importing
// `useRecsProvider` (transitively, via `MiniCartRecommendationWidget` above) so
// the theme section's cart-update seed retargeter can drive this widget on
// cart changes. See `src/search/useSearchProvider.ts` for the install + API.
// Bootstrap entry for the cart-drawer "Complete the Look" widget.
//
// Mount contract (mirrors data-recommendation-app):
//   <div
//     data-mini-cart-widget
//     data-recommendation-type="frequently-bought-together"
//     data-widget-id="cart-123"
//     data-page-type="cart_page"
//     data-limit="6"
//     data-title="Complete the Look"
//     [data-seed-product-id="…"]
//     [data-cart-drawer-selector="cart-drawer"]     <!-- override host selector -->
//     [data-mobile-slot-selector=".wt-cart__drawer__body"]  <!-- where to portal on mobile -->
//     [data-mobile-breakpoint-px="768"]
//   ></div>
//
// The authored mount is where the widget lives on DESKTOP (a side panel sibling
// of <cart-drawer>). On MOBILE the widget teleports via createPortal into a
// JS-created slot appended to the drawer's scroll body — so the widget scrolls
// with the cart's line items and can leave the viewport, without requiring any
// markup changes inside <cart-drawer> itself.
//
// The placement decision runs on each cart-open transition (observing the
// `open` attribute on <cart-drawer>) AND whenever the viewport crosses the
// mobile breakpoint (via a `matchMedia` listener). This keeps the widget
// correctly placed through dev-tools device emulation, window resize, and
// orientation-change flows — including while the cart is open.
const MOBILE_SLOT_ATTR = 'data-mini-cart-widget-slot';
function MiniCartWidgetMount({ desktopMountElement, cartDrawerElement, mobileSlotSelector, mobileBreakpointPx, recsConfigProps, }) {
    const [isDrawerOpen, setIsDrawerOpen] = useState(() => cartDrawerElement.hasAttribute('open'));
    // null → render into `desktopMountElement` (desktop side panel).
    // HTMLElement → portal into this host (mobile — inside the drawer body).
    const [mobilePortalHost, setMobilePortalHost] = useState(null);
    const mobileSlotRef = useRef(null);
    // Re-decide placement on three triggers:
    //   - drawer open/close      (MutationObserver on the `open` attribute)
    //   - viewport breakpoint    (matchMedia change)
    //   - cart contents updated  (`cart:updated` event — the theme's
    //                             renderContents replaces the drawer body's
    //                             innerHTML, which destroys our portal slot;
    //                             this lets us recreate it without remounting)
    useEffect(() => {
        const decidePlacement = () => {
            const isMobile = window.matchMedia(`(max-width: ${mobileBreakpointPx}px)`).matches;
            if (!isMobile) {
                setMobilePortalHost(null);
                return;
            }
            // Reuse the existing slot if it's still in the DOM (e.g. from a prior
            // open cycle); create a new one only when missing or the theme tore it
            // out by re-rendering the drawer.
            let slot = mobileSlotRef.current;
            if (!slot || !document.contains(slot)) {
                const mobileSlotContainer = cartDrawerElement.querySelector(mobileSlotSelector);
                if (!mobileSlotContainer) {
                    // No drawer body found — fall back to desktop placement rather than
                    // losing the widget entirely. Noisy in dev; silent in prod.
                    console.warn(`[mini-cart-widget] Mobile slot "${mobileSlotSelector}" not found — using desktop mount.`);
                    setMobilePortalHost(null);
                    return;
                }
                slot = document.createElement('div');
                slot.setAttribute(MOBILE_SLOT_ATTR, '');
                mobileSlotContainer.appendChild(slot);
                mobileSlotRef.current = slot;
            }
            setMobilePortalHost(slot);
        };
        const syncFromDrawerState = () => {
            const open = cartDrawerElement.hasAttribute('open');
            setIsDrawerOpen(open);
            if (open)
                decidePlacement();
        };
        syncFromDrawerState();
        const observer = new MutationObserver(syncFromDrawerState);
        observer.observe(cartDrawerElement, { attributes: true, attributeFilter: ['open'] });
        const breakpointQuery = window.matchMedia(`(max-width: ${mobileBreakpointPx}px)`);
        const handleBreakpointChange = () => decidePlacement();
        breakpointQuery.addEventListener('change', handleBreakpointChange);
        // Cart-updated re-attach: defer one frame so the theme's `renderContents`
        // (which fires synchronously on the same tick that dispatches
        // `cart:updated`) has finished swapping the drawer body innerHTML before
        // we look for the slot container.
        const handleCartUpdated = () => {
            requestAnimationFrame(() => {
                if (cartDrawerElement.hasAttribute('open'))
                    decidePlacement();
            });
        };
        document.addEventListener('cart:updated', handleCartUpdated);
        return () => {
            observer.disconnect();
            breakpointQuery.removeEventListener('change', handleBreakpointChange);
            document.removeEventListener('cart:updated', handleCartUpdated);
            // Remove the JS-created slot on unmount so a hot-reload / full teardown
            // doesn't leave stray elements inside the drawer body.
            if (mobileSlotRef.current && document.contains(mobileSlotRef.current)) {
                mobileSlotRef.current.remove();
            }
            mobileSlotRef.current = null;
        };
    }, [cartDrawerElement, mobileSlotSelector, mobileBreakpointPx]);
    const widgetElement = (h(MiniCartRecommendationWidget, { isVisible: isDrawerOpen, layout: mobilePortalHost ? 'accordion-carousel' : 'side-panel', seedProductId: recsConfigProps.seedProductId, recsType: recsConfigProps.recsType, title: recsConfigProps.title, recsOptions: recsConfigProps.recsOptions, mountElement: desktopMountElement }));
    // Always-portal: keeps the VNode type stable across breakpoint flips so
    // Preact updates the portal's container prop instead of remounting the
    // inner widget. That preserves all in-component state (refs, useState,
    // products, scroll position) on resize. Portaling into desktopMountElement
    // when there's no mobile slot looks recursive but isn't — createPortal
    // uses Preact's sub-render machinery, not the top-level render() API.
    return createPortal(widgetElement, mobilePortalHost !== null && mobilePortalHost !== void 0 ? mobilePortalHost : desktopMountElement);
}
document.querySelectorAll('[data-mini-cart-widget]').forEach(mountElement => {
    const cartDrawerSelector = mountElement.dataset.cartDrawerSelector || 'cart-drawer';
    const mobileSlotSelector = mountElement.dataset.mobileSlotSelector || '.wt-cart__drawer__body';
    const mobileBreakpointPx = Number(mountElement.dataset.mobileBreakpointPx) || 768;
    const cartDrawerElement = mountElement.closest(cartDrawerSelector) ||
        document.querySelector(cartDrawerSelector);
    if (!cartDrawerElement) {
        console.warn(`[mini-cart-widget] No "${cartDrawerSelector}" host element found — widget will stay hidden.`);
        return;
    }
    render(h(MiniCartWidgetMount, { desktopMountElement: mountElement, cartDrawerElement: cartDrawerElement, mobileSlotSelector: mobileSlotSelector, mobileBreakpointPx: mobileBreakpointPx, recsConfigProps: {
            seedProductId: mountElement.dataset.seedProductId,
            recsType: mountElement.dataset.recommendationType,
            title: mountElement.dataset.title,
            // Everything else (widgetId, pageType, limit, modelType, ruleBased, …)
            // is picked up by the widget via `readBoostRecsOptionsFromDataset(mountElement)`.
            recsOptions: {},
        } }), mountElement);
});
